import { CtaButton } from '@deckee/api-app-main';
import { Box, Button, MessageBlock, Card, Paragraph } from '@deckee/deck-hand';
import React, { MouseEvent, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { captureEvent } from '../../../../services/event-capture';
import { updateSelectedMarker } from '../../../../services/store/modules/app-controls';
import SheetLayout, {
  SheetBody,
} from '../../../layouts/SheetLayout/SheetLayout';
import { HeaderBarHeading } from '../../../layouts/components/HeaderBar/HeaderBar';
import LogoSpinner from '../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import VerifiedBadge from '../components/VerifiedBadge/VerifiedBadge';
import useDistanceFormatting from '../hooks/use-distance-formatting';
import useMoveMapToLocation from '../hooks/use-move-map-to-location';
import useSelectedListing from '../hooks/use-selected-listing';

const MarketplaceListingSummaryPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const [listing, , error] = useSelectedListing(id);

  const ctaButtons = listing ? listing.ctaButtons : [];
  const loaded = !!listing;
  const formatDistance = useDistanceFormatting();
  const fullSummary = listing?.summary || '';

  let summary = fullSummary.slice(0, 130);

  if (fullSummary.length > 130) {
    summary = `${summary}...`;
  }

  const distance =
    listing?.distanceFromRequestLocation &&
    formatDistance(listing?.distanceFromRequestLocation);

  const handleCtaButtonClick = (ctaButton: CtaButton) => {
    captureEvent({
      type: 'marketplace: cta button click summary view',
      payload: ctaButton,
      sendToAmplitude: true,
    });
  };
  const ref = useRef<HTMLDivElement>(null);

  const client = ref?.current || { clientHeight: 0, clientWidth: 0 };
  const sheetHeight = client.clientHeight;
  const sheetWidth = client.clientWidth;

  // Hard coded pixel size of when the app switches to mobile size
  const isMobile = (window.innerWidth || 0) <= 800;

  // For desktop, offset a little to the right
  // For mobile, offset towards the top
  useMoveMapToLocation({
    center: {
      lat: listing?.location.latitude,
      lon: listing?.location.longitude,
    },
    offsetY: isMobile ? sheetHeight * 0.45 : 0,
    offsetX: !isMobile ? -sheetWidth * 0.45 : 0,
  });

  const handleViewDetailsClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    captureEvent({
      type: 'marketplace: listing summary view click view details',
      payload: {},
      sendToAmplitude: true,
    });

    history.push(`/maps/marketplace/listing/${listing?.id}`);
  };

  useEffect(() => {
    if (listing && listing.isMarker) {
      dispatch(
        updateSelectedMarker({
          selectedMarkerId: listing.id,
          mapMarkerType: listing.markerType,
        }),
      );
    }
  }, [dispatch, listing]);

  return (
    <SheetLayout
      heading={
        <Box display="flex" alignItems="center">
          <HeaderBarHeading pl="2" level="5">
            {listing?.name || ''}
          </HeaderBarHeading>
          {listing?.partner && <VerifiedBadge badgeOnly />}
        </Box>
      }
      headingPosition="center"
      showMenuBar={false}
    >
      <SheetBody ref={ref}>
        {error && (
          <MessageBlock type="error" mb="3">
            {error}
          </MessageBlock>
        )}
        {loaded && (
          <Card>
            <Card.Body>
              <Paragraph>{summary}</Paragraph>
              {distance && (
                <Paragraph color="darkergrey">{distance} away</Paragraph>
              )}

              <Box
                mt="3"
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                flexWrap="wrap"
              >
                <Button mt="2" onClick={handleViewDetailsClick}>
                  View details
                </Button>
                {ctaButtons.map((button) => (
                  <Button
                    mt="2"
                    ml="2"
                    key={button.label}
                    outlined={button.type === 'secondary'}
                    href={button.url}
                    onClick={() => handleCtaButtonClick(button)}
                  >
                    {button.label}
                  </Button>
                ))}
              </Box>
            </Card.Body>
          </Card>
        )}

        {!loaded && <LogoSpinner />}
      </SheetBody>
    </SheetLayout>
  );
};

export default MarketplaceListingSummaryPage;
